'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useSwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';
import { FaTimes } from 'react-icons/fa';

interface GallerySlideProps {
  image: string;
  title: string;
}

const GallerySlide: React.FC<GallerySlideProps> = ({ image, title }) => {
  const swiperSlide = useSwiperSlide();
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);

  // Show overlay again when slide becomes active (new swipe)
  useEffect(() => {
    if (swiperSlide.isActive) {
      setIsOverlayVisible(true);
    }
  }, [swiperSlide.isActive]);

  return (
    <div className="group h-full w-full px-4 md:px-0">
      <div
        className={twMerge(
          'relative aspect-video h-full w-full rounded-2xl shadow-lg overflow-hidden mx-auto',
          'transition-all duration-700 ease-in-out',
          swiperSlide.isActive
            ? 'scale-100 brightness-100 blur-0'
            : 'scale-70 brightness-60 blur-sm md:scale-70'
        )}
      >
        <Image
          src={image}
          alt={title}
          layout="fill"
          objectFit="cover"
          className="transition-all duration-500"
        />
        {/* Dark gradient overlay for better text readability */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent h-12 md:h-16 pointer-events-none" />
        
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
          <div
            className={twMerge(
              'bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg p-2 md:p-3 relative',
              'transition-all duration-500',
              swiperSlide.isActive && isOverlayVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            )}
          >
            <h3 className="text-white text-base md:text-lg font-bold text-center truncate drop-shadow-lg pr-6 md:pr-0">
              {title}
            </h3>
            {/* Hide button - only on mobile */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOverlayVisible(false);
              }}
              className="absolute top-1/2 -translate-y-1/2 right-1 text-white/70 hover:text-white transition-colors p-1 md:hidden"
              aria-label="Hide title"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GallerySlide;
